// This is to show an entire distribution location's order.
// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import Button from "@material-ui/core/Button";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import SortWholeCommunityOrders from "../Functions/SortWholeCommunityOrders.js";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import WholeOrderAccordion from "../Components/Accordions/WholeOrderAccordion.jsx";
import CreateDownloadLocationList from "../../../pages/MyAccountPage/Functions/CreateDownloadLocationList.js";
import CreateDownloadWholeCommunityOrdersString from "../../../pages/MyAccountPage/Functions/CreateDownloadWholeCommunityOrdersString.js";
import DownloadOrders from "../../../components/dialogs/functions/DownloadOrders.js";
import PrintOutSelectionDialog from "../../../components/dialogs/Components/DownloadOrders/PrintOutSelectionDialog.jsx";

// Filters the customerFoodListPerLocation and the sortedPickupLocations down to
// only the locations the user picked.  selectedLocationKeys are in the form
// "<distributionLocation> - <pickupLocation>" and map back to the pair through
// locationKeys.  Returns both filtered structures so they can be passed through
// to the string builders unchanged.
function filterLocations(
  selectedLocationKeys,
  locationKeys,
  customerFoodListPerLocation,
) {
  const filteredFoodList = {};
  const filteredSortedPickupLocations = {};

  selectedLocationKeys.forEach((key) => {
    const { distributionLocation, pickupLocation } = locationKeys[key];
    if (!filteredFoodList[distributionLocation]) {
      filteredFoodList[distributionLocation] = {};
      filteredSortedPickupLocations[distributionLocation] = [];
    }
    filteredFoodList[distributionLocation][pickupLocation] =
      customerFoodListPerLocation[distributionLocation][pickupLocation];
    filteredSortedPickupLocations[distributionLocation].push(pickupLocation);
  });

  return { filteredFoodList, filteredSortedPickupLocations };
}

// This is the "type" picker dialog that mirrors OrderDownloadSelectionDialog.
// It shows two buttons - one per download format - and each one opens its own
// PrintOutSelectionDialog to pick which locations to include.
function CommunityOrdersDownloadSelectionBox({
  open,
  onClose,
  customerFoodListPerLocation,
  sortedDistributionLocations,
  sortedPickupLocations,
}) {
  const handleClose = () => {
    onClose(false);
  };

  // Build a lookup of "<distribution> - <pickup>" -> {distributionLocation,
  // pickupLocation}.  The keys of this object are what's shown as the
  // checkbox labels in PrintOutSelectionDialog.
  const locationKeys = {};
  sortedDistributionLocations.forEach((distributionLocation) => {
    sortedPickupLocations[distributionLocation].forEach((pickupLocation) => {
      locationKeys[`${distributionLocation} - ${pickupLocation}`] = {
        distributionLocation,
        pickupLocation,
      };
    });
  });

  // Runs once the user has picked the format and the locations.  For the
  // fridge and freezer mode we produce one combined grid file (unchanged).
  // For the whole community orders we produce one word doc per location -
  // mirroring how the LIST FORMAT option in OrderDownloadSelectionDialog
  // downloads a separate file per pickup location.
  const handleDownload = (mode, selectedLocationKeys) => {
    const { filteredFoodList, filteredSortedPickupLocations } = filterLocations(
      selectedLocationKeys,
      locationKeys,
      customerFoodListPerLocation,
    );

    if (mode === "fridgeFreezer") {
      const downloadStr = CreateDownloadLocationList(
        filteredSortedPickupLocations,
        filteredFoodList,
      );
      DownloadOrders(downloadStr, "", "Fridge-Freezer", "Items-Per-Location");
    } else {
      // One html string per location, keyed by "<distribution> - <pickup>".
      const downloadStringsByLocation =
        CreateDownloadWholeCommunityOrdersString(
          filteredSortedPickupLocations,
          filteredFoodList,
        );
      selectedLocationKeys.forEach((key) => {
        const { pickupLocation } = locationKeys[key];
        // Name each file after the hub/onsite name so the downloaded files
        // read like "Onsite-Whole-Community-Orders.doc".
        DownloadOrders(
          downloadStringsByLocation[key],
          "",
          pickupLocation,
          "Whole-Community-Orders",
          true,
        );
      });
    }
    onClose(true);
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="community-orders-download-title"
      open={open}
    >
      <DialogTitle id="community-orders-download-title">
        What Would You Like To Download?
      </DialogTitle>
      <DialogContent>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <PrintOutSelectionDialog
            downloadString={locationKeys}
            handleDownload={(_downloadString, selectedLocationKeys) =>
              handleDownload("fridgeFreezer", selectedLocationKeys)
            }
            buttonText="FRIDGE AND FREEZER ITEMS PER LOCATION"
            getLabel={(key) => locationKeys[key].pickupLocation}
          />
          <PrintOutSelectionDialog
            downloadString={locationKeys}
            handleDownload={(_downloadString, selectedLocationKeys) =>
              handleDownload("whole", selectedLocationKeys)
            }
            buttonText="WHOLE COMMUNITY ORDERS"
            getLabel={(key) => locationKeys[key].pickupLocation}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handleClose}>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CommunityOrdersDownloadSelectionBox.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerFoodListPerLocation: PropTypes.object.isRequired,
  sortedDistributionLocations: PropTypes.array.isRequired,
  sortedPickupLocations: PropTypes.object.isRequired,
};

// This is the dialog box that opens when a user clicks on View Customer Receipts
function ViewCommunityOrdersBox({
  open,
  onClose,
  customerFoodListPerLocation,
}) {
  const handleClose = () => {
    onClose(false);
  };

  // This is to show the button or not.  It was implemented to allow for copy and
  // pasting to not have the big button in it.
  const [showCustomersButton, setShowCustomersButton] = useState(true);

  // Controls whether the type-selection download dialog is open.
  const [downloadOpen, setDownloadOpen] = useState(false);

  // We want to sort the distribution locations alphabetically so they appear in the same order.
  const sortedDistributionLocations = Object.keys(
    customerFoodListPerLocation,
  ).sort();

  // We want to sort the pickupLocations alphabetically but with the Onsite appearing first.
  const sortedPickupLocations = {};

  // Cycle through the distribution locations.
  sortedDistributionLocations.forEach((distributionLocation) => {
    // Sort the pickupLocations
    sortedPickupLocations[distributionLocation] = Object.keys(
      customerFoodListPerLocation[distributionLocation],
    ).sort();
    // Find the index of the Onsite pickupLocation in the array.
    const indexOfOnsite =
      sortedPickupLocations[distributionLocation].indexOf("Onsite");
    // If it exists then
    if (indexOfOnsite !== -1) {
      // remove it
      sortedPickupLocations[distributionLocation].splice(indexOfOnsite, 1);
      // and add it back to the start of the array.
      sortedPickupLocations[distributionLocation].unshift("Onsite");
    }
  });

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle id="simple-dialog-title">
        Whole Community Orders By Location
        <FormControlLabel
          label="Toggle Customer Buttons"
          control={
            <Switch
              checked={showCustomersButton}
              onChange={() => setShowCustomersButton(!showCustomersButton)}
            />
          }
        />
      </DialogTitle>
      <DialogContent>
        {sortedDistributionLocations.map((distributionLocation, y) => (
          <span key={y}>
            {sortedPickupLocations[distributionLocation].map(
              (pickupLocation, i) => (
                <WholeOrderAccordion
                  key={i}
                  distributionLocation={distributionLocation}
                  pickupLocation={pickupLocation}
                  customerFoodListPerLocation={customerFoodListPerLocation}
                  showCustomersButton={showCustomersButton}
                />
              ),
            )}
          </span>
        ))}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handleClose}>
          CLOSE
        </Button>
        <Button color="primary" onClick={() => setDownloadOpen(true)}>
          DOWNLOAD
        </Button>
      </DialogActions>

      <CommunityOrdersDownloadSelectionBox
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        customerFoodListPerLocation={customerFoodListPerLocation}
        sortedDistributionLocations={sortedDistributionLocations}
        sortedPickupLocations={sortedPickupLocations}
      />
    </Dialog>
  );
}

ViewCommunityOrdersBox.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerFoodListPerLocation: PropTypes.object.isRequired,
};

export default function ViewCommunityOrdersDialog({
  communityOrders,
  importedOrder,
  organisationName,
}) {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Send the community orders and the imported orders to be put into one list where
  // the food items are per location and the pantry items are per location.
  const customerFoodListPerLocation = SortWholeCommunityOrders(
    communityOrders,
    importedOrder,
    organisationName,
  );

  return (
    <div>
      <Button size="small" color="primary" onClick={handleClickOpen}>
        VIEW THE COMMUNITY&quot;S ORDER AS A WHOLE
      </Button>

      <ViewCommunityOrdersBox
        open={open}
        onClose={handleClose}
        customerFoodListPerLocation={customerFoodListPerLocation}
      />
    </div>
  );
}
